/**
 * Storage Manager Utility
 * Handles localStorage operations with quota management and error handling
 */

// Storage limits (in bytes) - conservative estimates
const STORAGE_LIMITS = {
  localStorage: 4 * 1024 * 1024, // 4MB conservative limit
  sessionStorage: 4 * 1024 * 1024, // 4MB conservative limit
};

// Priority levels for storage cleanup
const STORAGE_PRIORITY = {
  CRITICAL: 1,    // Essential data (user auth, active cart)
  HIGH: 2,        // Important but replaceable (user preferences)
  MEDIUM: 3,      // Cached data that can be refetched
  LOW: 4,         // Temporary data, analytics
};

// Storage key patterns and their priorities
const STORAGE_KEY_PRIORITIES = {
  // Critical - never remove
  'token': STORAGE_PRIORITY.CRITICAL,
  'cartData': STORAGE_PRIORITY.CRITICAL,
  'user': STORAGE_PRIORITY.CRITICAL,
  'authData': STORAGE_PRIORITY.CRITICAL,
  
  // High priority
  'userPreferences': STORAGE_PRIORITY.HIGH,
  'language': STORAGE_PRIORITY.HIGH,
  'theme': STORAGE_PRIORITY.HIGH,
  
  // Medium priority
  'searchHistory': STORAGE_PRIORITY.MEDIUM,
  'courseProgress': STORAGE_PRIORITY.MEDIUM,
  'recentCourses': STORAGE_PRIORITY.MEDIUM,
  
  // Low priority - can be safely removed
  'analytics': STORAGE_PRIORITY.LOW,
  'temp': STORAGE_PRIORITY.LOW,
  'cache': STORAGE_PRIORITY.LOW,
  'draft': STORAGE_PRIORITY.LOW,
  'session': STORAGE_PRIORITY.LOW,
};

/**
 * Calculate the size of a value in bytes
 */
function calculateSize(value) {
  if (typeof value === 'string') {
    return new Blob([value]).size;
  }
  return new Blob([JSON.stringify(value)]).size;
}

/**
 * Calculate total storage usage
 */
function calculateStorageUsage(storageType = 'localStorage') {
  const storage = storageType === 'localStorage' ? localStorage : sessionStorage;
  let totalSize = 0;
  const itemSizes = {};

  for (let key in storage) {
    if (storage.hasOwnProperty(key)) {
      const value = storage[key];
      const size = calculateSize(value) + calculateSize(key);
      itemSizes[key] = size;
      totalSize += size;
    }
  }

  return {
    totalSize,
    itemSizes,
    percentageUsed: (totalSize / STORAGE_LIMITS[storageType]) * 100,
    remainingSpace: STORAGE_LIMITS[storageType] - totalSize,
  };
}

/**
 * Get priority of a storage key
 */
function getKeyPriority(key) {
  // Check exact matches first
  if (STORAGE_KEY_PRIORITIES[key]) {
    return STORAGE_KEY_PRIORITIES[key];
  }
  
  // Check pattern matches
  for (const pattern in STORAGE_KEY_PRIORITIES) {
    if (key.includes(pattern)) {
      return STORAGE_KEY_PRIORITIES[pattern];
    }
  }
  
  // Default to medium priority
  return STORAGE_PRIORITY.MEDIUM;
}

/**
 * Clean up storage based on priority and size
 */
function cleanupStorage(storageType = 'localStorage', targetReduction = 0.3) {
  const storage = storageType === 'localStorage' ? localStorage : sessionStorage;
  const usage = calculateStorageUsage(storageType);
  const targetSize = usage.totalSize * (1 - targetReduction);
  
  console.log(`🧹 Starting storage cleanup. Current: ${formatBytes(usage.totalSize)}, Target: ${formatBytes(targetSize)}`);
  
  // Get all items with their priorities and sizes
  const items = [];
  for (let key in storage) {
    if (storage.hasOwnProperty(key)) {
      items.push({
        key,
        size: usage.itemSizes[key],
        priority: getKeyPriority(key),
      });
    }
  }
  
  // Sort by priority (higher number = lower priority = remove first)
  // Then by size (larger items first within same priority)
  items.sort((a, b) => {
    if (a.priority !== b.priority) {
      return b.priority - a.priority; // Higher priority number = remove first
    }
    return b.size - a.size; // Larger size first
  });
  
  let currentSize = usage.totalSize;
  const removedItems = [];
  
  // Remove items until we reach target size
  for (const item of items) {
    if (currentSize <= targetSize) break;
    if (item.priority === STORAGE_PRIORITY.CRITICAL) continue; // Never remove critical items
    
    try {
      storage.removeItem(item.key);
      currentSize -= item.size;
      removedItems.push(item);
      console.log(`🗑️ Removed ${item.key} (${formatBytes(item.size)}, priority: ${item.priority})`);
    } catch (error) {
      console.warn(`⚠️ Failed to remove ${item.key}:`, error);
    }
  }
  
  const newUsage = calculateStorageUsage(storageType);
  console.log(`✅ Cleanup complete. Before: ${formatBytes(usage.totalSize)}, After: ${formatBytes(newUsage.totalSize)}`);
  
  return {
    removedItems,
    sizeBefore: usage.totalSize,
    sizeAfter: newUsage.totalSize,
    spaceFreed: usage.totalSize - newUsage.totalSize,
  };
}

/**
 * Safe storage setter with automatic cleanup
 */
export function safeSetItem(key, value, storageType = 'localStorage', retryCount = 0) {
  const storage = storageType === 'localStorage' ? localStorage : sessionStorage;
  const maxRetries = 2;
  
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    storage.setItem(key, stringValue);
    return { success: true, error: null };
  } catch (error) {
    console.warn(`⚠️ Storage error for key "${key}":`, error.message);
    
    if (error.name === 'QuotaExceededError' && retryCount < maxRetries) {
      console.log(`🔄 Attempting storage cleanup (attempt ${retryCount + 1}/${maxRetries})`);
      
      // Try cleanup and retry
      const cleanup = cleanupStorage(storageType, 0.4); // Free up 40% of space
      
      if (cleanup.spaceFreed > 0) {
        console.log(`🆓 Freed ${formatBytes(cleanup.spaceFreed)} of space`);
        return safeSetItem(key, value, storageType, retryCount + 1);
      }
    }
    
    // If all retries failed or it's not a quota error
    return { 
      success: false, 
      error: error.message,
      isQuotaError: error.name === 'QuotaExceededError',
      retryCount 
    };
  }
}

/**
 * Safe storage getter with error handling
 */
export function safeGetItem(key, defaultValue = null, storageType = 'localStorage') {
  const storage = storageType === 'localStorage' ? localStorage : sessionStorage;
  
  try {
    const value = storage.getItem(key);
    if (value === null) return defaultValue;
    
    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  } catch (error) {
    console.warn(`⚠️ Error getting storage item "${key}":`, error.message);
    return defaultValue;
  }
}

/**
 * Safe storage remover
 */
export function safeRemoveItem(key, storageType = 'localStorage') {
  const storage = storageType === 'localStorage' ? localStorage : sessionStorage;
  
  try {
    storage.removeItem(key);
    return { success: true, error: null };
  } catch (error) {
    console.warn(`⚠️ Error removing storage item "${key}":`, error.message);
    return { success: false, error: error.message };
  }
}

/**
 * Format bytes to human readable format
 */
export function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Export the main API
export default {
  safeSetItem,
  safeGetItem,
  safeRemoveItem,
  cleanupStorage,
  calculateStorageUsage,
  formatBytes,
};
